import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import { Stack } from '@mui/system'
import React from 'react'
import { useStoreState } from '../../../../store/hooks'
import CircularButton from '../../../analysisView/components/analyisControls/CircularButton'
import { EmoteButton } from '../../emotes/EmoteButton'
import { selectEmoteSlot } from '../../emotes/EmotesModel'
import { ControlSlot, useMotionMs } from './ControlSlot'
import AbortIcon from './icons/Abort'
import DrawIcon from './icons/Draw'
import FlipIcon from './icons/Flip'
import ResignIcon from './icons/Resign'

const GUTTER = '0 12px' // 24px between buttons
const STAGGER_MS = 40

export type GameEndControlsProps = {
    onResignClick: () => void
    onDrawClick: () => void
    onAbortClick: () => void
    onFlipClick: () => void
    setSentOfferDraw: (setSentOfferDraw: boolean) => void
    vsHuman: boolean
    drawEnabled: boolean
    abortEnabled: boolean
    sentOfferDraw: boolean
    disabled: boolean
    // after a PvP game only Emote stays (where the player had it); the game actions fold away
    gameOver?: boolean
}
export const GameEndControls = React.memo((props: GameEndControlsProps) => {
    const {
        onResignClick,
        onDrawClick,
        onAbortClick,
        onFlipClick,
        setSentOfferDraw,
        sentOfferDraw,
        vsHuman,
        drawEnabled,
        abortEnabled,
        disabled,
        gameOver = false,
    } = props
    const [drawTimer, setDrawTimer] = React.useState(0)
    const emoteShown = useStoreState((state) => selectEmoteSlot(state) !== 'hidden')
    const motionMs = useMotionMs()

    // drawTimer
    React.useEffect(() => {
        let intervalIdDraw: NodeJS.Timeout
        if (sentOfferDraw && drawTimer === 0) {
            setDrawTimer(100)
        }
        if (drawEnabled && drawTimer > 0) {
            intervalIdDraw = setInterval(() => {
                setDrawTimer(drawTimer - 1)
            }, 600)
            if (drawTimer === 1) {
                setSentOfferDraw(false)
                setDrawTimer(0)
            }
        }

        return () => {
            clearInterval(intervalIdDraw)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sentOfferDraw, drawTimer])

    // buttons fade in one after another when the row appears; Abort (and Emote without a list) fold away later,
    // and at game over everything but Emote folds (the whole row too when there are no post-game emotes)
    return (
        <Collapse in={!gameOver || emoteShown} timeout={motionMs} unmountOnExit sx={{ flexShrink: 0 }}>
            <Stack direction="row" justifyContent="center">
                {vsHuman && <EmoteButton disabled={disabled && !gameOver} gutter={GUTTER} />}

                <ControlSlot show={!gameOver} gutter={GUTTER} delay={STAGGER_MS}>
                    <CircularButton disabled={disabled} title="Resign" onClick={onResignClick} icon={<ResignIcon />} />
                </ControlSlot>

                <ControlSlot show={!gameOver} gutter={GUTTER} delay={STAGGER_MS * 2}>
                    <CircularButton disabled={disabled} title="Flip" onClick={onFlipClick} icon={<FlipIcon />} />
                </ControlSlot>

                {vsHuman && (
                    <>
                        <ControlSlot show={!gameOver} gutter={GUTTER} delay={STAGGER_MS * 3}>
                            <Box style={{ position: 'relative' }}>
                                <CircularProgress
                                    className="circularProgress"
                                    variant="determinate"
                                    value={drawTimer}
                                    size={'40px'}
                                    style={{
                                        display: drawEnabled ? 'block' : 'none',
                                        zIndex: 4, // set a lower z-index value than the abort button
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        pointerEvents: 'none',
                                    }}
                                />
                                <CircularButton
                                    title="Draw"
                                    onClick={() => onDrawClick()}
                                    icon={<DrawIcon />}
                                    disabled={!drawEnabled || sentOfferDraw || disabled}
                                />
                            </Box>
                        </ControlSlot>

                        <ControlSlot show={abortEnabled && !gameOver} gutter={GUTTER} delay={STAGGER_MS * 4}>
                            <CircularButton
                                title="Abort"
                                onClick={onAbortClick}
                                icon={<AbortIcon />}
                                disabled={!abortEnabled || disabled}
                            />
                        </ControlSlot>
                    </>
                )}
            </Stack>
        </Collapse>
    )
})
